import React, { useCallback, useEffect, useState } from 'react';
import { latencyProfilerApi, PhaseSummary, RecentCall, StagesResponse } from './latencyProfilerApi';

const PHASE_LABELS: Record<string, string> = {
  endpointing: 'Endpointing (VAD → STT final)',
  context: 'Context load (STT → context ready)',
  memory: 'Memory retrieval',
  llm: 'LLM (first token)',
  tts: 'TTS (first audio)',
  playout: 'Playout (→ customer heard)',
};

const WINDOWS: { label: string; hours: number }[] = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

function ms(v?: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${Math.round(v)}ms`;
}

function goodColor(p95: number): string {
  // Target sub-1.5s end-to-end; color phases relative to their share.
  if (p95 >= 1500) return 'bg-rose-500';
  if (p95 >= 700) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function Panel({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-enterprise-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-enterprise-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-enterprise-100 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-enterprise-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-enterprise-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-enterprise-500">{hint}</p>}
    </div>
  );
}

const LatencyProfilerPage: React.FC = () => {
  const [hours, setHours] = useState<number>(168);
  const [stages, setStages] = useState<StagesResponse | null>(null);
  const [calls, setCalls] = useState<RecentCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([
        latencyProfilerApi.stages(hours),
        latencyProfilerApi.recentCalls(hours),
      ]);
      setStages(((s as any)?.data ?? s) as StagesResponse);
      setCalls((((c as any)?.data ?? c) as any)?.calls ?? []);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to load latency metrics');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    void load();
  }, [load]);

  const phases: PhaseSummary[] = stages?.phases ?? [];
  const maxP95 = Math.max(1, ...phases.map((p) => p.p95 || 0));
  const e2e = stages?.e2e_ms;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-enterprise-900">Voice Latency Profiler</h1>
          <p className="text-sm text-enterprise-500">
            Per-stage response latency for AI calls (P50 / P95 / P99). Target end-to-end &lt; 1.5s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border border-enterprise-200">
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                type="button"
                onClick={() => setHours(w.hours)}
                className={`px-3 py-1.5 text-sm ${
                  hours === w.hours ? 'bg-enterprise-900 text-white' : 'bg-white text-enterprise-700'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg bg-enterprise-900 px-3 py-1.5 text-sm text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {!loading && !error && (
        <>
          <Panel title="End-to-end (user stopped → customer heard)">
            {!stages || stages.turns === 0 ? (
              <p className="text-sm text-enterprise-500">
                No latency turns recorded in this window yet. Place an AI call to populate metrics.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="P50" value={ms(e2e?.p50)} hint={`${stages.turns} turns`} />
                <StatCard label="P95" value={ms(e2e?.p95)} />
                <StatCard label="P99" value={ms(e2e?.p99)} />
                <StatCard label="Max" value={ms(e2e?.max)} />
              </div>
            )}
          </Panel>

          <Panel title="Per-stage breakdown">
            {phases.length === 0 ? (
              <p className="text-sm text-enterprise-500">No stage data.</p>
            ) : (
              <div className="space-y-3">
                {phases.map((p) => (
                  <div key={p.phase}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-enterprise-800">
                        {PHASE_LABELS[p.phase] || p.phase}
                      </span>
                      <span className="text-enterprise-500">
                        P50 {ms(p.p50)} · P95 {ms(p.p95)} · P99 {ms(p.p99)} · n={p.n}
                      </span>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-enterprise-100">
                      <div
                        className={`h-full rounded-full ${goodColor(p.p95)}`}
                        style={{ width: `${Math.min(100, ((p.p95 || 0) / maxP95) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Recent AI calls">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-enterprise-500">
                  <tr>
                    <th className="py-2 pr-4">Session</th>
                    <th className="py-2 pr-4">Turns</th>
                    <th className="py-2 pr-4">E2E P50</th>
                    <th className="py-2 pr-4">E2E P95</th>
                    <th className="py-2 pr-4">E2E Max</th>
                    <th className="py-2">Last turn</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.session_id} className="border-t border-enterprise-100">
                      <td className="py-2 pr-4 font-mono text-xs text-enterprise-700">
                        {c.session_id.slice(0, 12)}…
                      </td>
                      <td className="py-2 pr-4">{c.turns}</td>
                      <td className="py-2 pr-4">{ms(c.e2e_p50_ms)}</td>
                      <td className="py-2 pr-4">{ms(c.e2e_p95_ms)}</td>
                      <td className="py-2 pr-4">{ms(c.e2e_max_ms)}</td>
                      <td className="py-2 text-enterprise-500">
                        {c.last_turn_at ? new Date(c.last_turn_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                  {calls.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-3 text-sm text-enterprise-500">
                        No calls in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
};

export default LatencyProfilerPage;
